import { useState, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { Answer } from "./components/Answer";
import { Footnotes } from "./components/Footnotes";
import { LoadingState } from "./components/LoadingState";
import { LawExplorer } from "./components/LawExplorer";
import { HomeView } from "./components/HomeView";
import { HistoryPanel } from "./components/HistoryPanel";
import { ChangelogPanel } from "./components/ChangelogPanel";
import { useStreamingAnswer } from "./hooks/useStreamingAnswer";
import { useHistory } from "./hooks/useHistory";
import "./styles.css";
import { UrsidaiLogo } from "./components/UrsidaiLogo";

export interface ArticleSource {
  kind?: "article";
  articleNum: string;
  idcc?: string;
  sectionPath: string;
  sectionIds?: string[];
  articleUrl: string;
  conventionTitle?: string;
  score: number;
}

export interface DecisionSource {
  kind: "decision";
  decisionId: string;
  articleNum: string;
  sectionPath: string;
  articleUrl: string;
  score: number;
  chamber?: string;
  decisionDate?: string;
}

export type Source = ArticleSource | DecisionSource;

interface ConversationEntry {
  id: string;
  question: string;
  answer: string;
  sources: Source[];
  timestamp: Date;
  isComplete: boolean;
}

export default function App() {
  const [currentEntry, setCurrentEntry] = useState<ConversationEntry | null>(null);
  const [explorerArticleId, setExplorerArticleId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const { isStreaming, streamAnswer } = useStreamingAnswer();
  const { history, addItem, updateItem, findByQuestion, removeItem, clearHistory } = useHistory();

  const handleSourceClick = useCallback(
    (url: string) => {
      if (url.startsWith("judilibre:")) {
        const decisionId = url.slice("judilibre:".length);
        void navigator.clipboard?.writeText(decisionId).catch(() => {});
        return;
      }
      // Extract article ID from Legifrance URL
      const kaliMatch = url.match(/\/conv_coll\/id\/([A-Z0-9]+)/);
      const legiArticleMatch = url.match(/\/codes\/article_lc\/([A-Z0-9]+)/);
      const legiSectionMatch = url.match(
        /\/codes\/section_lc\/[A-Z0-9]+\/([A-Z0-9]+)\/#([A-Z0-9]+)/,
      );

      const articleId = kaliMatch?.[1] || legiArticleMatch?.[1] || legiSectionMatch?.[2];
      console.log(
        "handleSourceClick",
        url,
        kaliMatch,
        legiArticleMatch,
        legiSectionMatch,
        articleId,
      );

      if (articleId && explorerArticleId !== articleId) {
        setExplorerArticleId(articleId);
      } else {
        // Fallback: open in new tab if we can't extract article ID
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [explorerArticleId],
  );

  const handleCloseExplorer = useCallback(() => {
    setExplorerArticleId(null);
  }, []);

  const handleReset = useCallback(() => {
    setCurrentEntry(null);
    setExplorerArticleId(null);
  }, []);

  const handleToggleHistory = useCallback(() => {
    setIsHistoryOpen((prev) => !prev);
  }, []);

  const handleToggleChangelog = useCallback(() => {
    setIsChangelogOpen((prev) => !prev);
  }, []);

  const handleSearch = useCallback(
    async (question: string) => {
      // Close history panel if open
      setIsHistoryOpen(false);

      // Check if we have a cached answer
      const cached = findByQuestion(question);
      if (cached && cached.answer) {
        // Restore from cache
        setCurrentEntry({
          id: cached.id,
          question: cached.question,
          answer: cached.answer,
          sources: cached.sources || [],
          timestamp: new Date(cached.timestamp),
          isComplete: true,
        });
        return;
      }

      // Add to history and get the ID
      const historyId = addItem(question);

      // Set initial entry
      setCurrentEntry({
        id: historyId,
        question,
        answer: "",
        sources: [],
        timestamp: new Date(),
        isComplete: false,
      });

      let finalAnswer = "";
      let finalSources: Source[] = [];

      // Stream the answer
      await streamAnswer(question, {
        onSources: (sources) => {
          finalSources = sources;
          setCurrentEntry((prev) => (prev ? { ...prev, sources } : null));
        },
        onChunk: (chunk) => {
          finalAnswer += chunk;
          setCurrentEntry((prev) => (prev ? { ...prev, answer: prev.answer + chunk } : null));
        },
        onDone: () => {
          setCurrentEntry((prev) => (prev ? { ...prev, isComplete: true } : null));
          // Save the complete answer to history
          updateItem(historyId, finalAnswer, finalSources);
        },
        onError: (error) => {
          setCurrentEntry((prev) =>
            prev
              ? {
                  ...prev,
                  answer: `Erreur: ${error}`,
                  isComplete: true,
                }
              : null,
          );
        },
      });
    },
    [streamAnswer, addItem, updateItem, findByQuestion],
  );

  const handleHistorySelect = useCallback(
    (question: string) => {
      handleSearch(question);
    },
    [handleSearch],
  );

  return (
    <div className="luminosity">
      {/* History Panel */}
      <HistoryPanel
        isOpen={isHistoryOpen}
        onToggle={handleToggleHistory}
        history={history}
        onSelectItem={handleHistorySelect}
        onRemoveItem={removeItem}
        onClearHistory={clearHistory}
      />

      {/* Changelog Panel */}
      <ChangelogPanel isOpen={isChangelogOpen} onToggle={handleToggleChangelog} />

      {/* Header with Logo */}
      <header className="header">
        <a
          href="/"
          style={{ textDecoration: "none", color: "inherit" }}
          onClick={(e) => {
            e.preventDefault();
            handleReset();
          }}
        >
          <div className="header-content">
            <div className="logo" style={{ display: "flex", justifyContent: "center" }}>
              Lex{" "}
              <span style={{ marginBottom: "1px" }}>
                <UrsidaiLogo width={40} height={40} />
              </span>
            </div>
            {/* <p className="tagline">Un assistant pour baiser la concu.</p> */}
          </div>
        </a>
      </header>

      {/* Main Content Area */}
      <main className="main">
        {!currentEntry ? (
          /* Home State - Centered Search */
          <HomeView onSearch={handleSearch} disabled={isStreaming} />
        ) : (
          /* Answer State */
          <div className="content-with-iframe">
            <div className="book-content">
              <div className="answer-view">
                {/* Question header - sticky on mobile */}
                <header className="question-header">
                  <h2 className="question-text">{currentEntry.question}</h2>
                </header>

                <div className="answer-body">
                  {currentEntry.answer ? (
                    <Answer
                      content={currentEntry.answer}
                      sources={currentEntry.sources}
                      isStreaming={!currentEntry.isComplete}
                      onSourceClick={handleSourceClick}
                    />
                  ) : (
                    <LoadingState />
                  )}
                </div>

                {/* Footnotes Section */}
                {currentEntry.sources.length > 0 && currentEntry.isComplete && (
                  <Footnotes sources={currentEntry.sources} onSourceClick={handleSourceClick} />
                )}

                {/* Export Actions
                {currentEntry.isComplete && currentEntry.answer && (
                    <ExportActions />
                )} */}

                {/* Back button - floating pill on mobile */}
                <button className="reset-button" onClick={handleReset}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Nouvelle recherche
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Law Explorer Panel */}
      <LawExplorer articleId={explorerArticleId} onClose={handleCloseExplorer} />
    </div>
  );
}

// Mount the app
const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}
